import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronsLeftRight, ArrowUpRight } from "lucide-react";
import { Reveal, SectionLabel, EASE } from "./Reveal";
import { scrollToId } from "./Navbar";
import { GALLERY_JOBS } from "@/data/gallery";

function CompareSlider({ job }) {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);

  const update = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(97, Math.max(3, pct)));
  };

  return (
    <div
      ref={ref}
      data-testid="compare-slider"
      className="relative aspect-[4/3] md:aspect-[16/10] overflow-hidden border border-line select-none touch-none cursor-ew-resize bg-ash"
      onPointerDown={(e) => {
        dragging.current = true;
        ref.current.setPointerCapture(e.pointerId);
        update(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && update(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
      onPointerCancel={() => (dragging.current = false)}
    >
      <img
        src={job.after}
        alt={`${job.title} — after painting`}
        draggable="false"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ filter: job.afterFilter }}
      />
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={job.before}
          alt={`${job.title} — before painting`}
          draggable="false"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: job.beforeFilter }}
        />
      </div>

      <span className="absolute top-5 left-5 bg-coal/70 backdrop-blur-sm text-[10px] uppercase tracking-[0.3em] text-bone/80 px-3 py-2">
        Before
      </span>
      <span className="absolute top-5 right-5 bg-ember text-[10px] uppercase tracking-[0.3em] font-semibold text-coal px-3 py-2">
        After
      </span>

      <div
        className="absolute inset-y-0 w-px bg-bone/90"
        style={{ left: `${pos}%` }}
        aria-hidden="true"
      />
      <div
        data-testid="compare-handle"
        role="slider"
        aria-label="Reveal before and after"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pos)}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") setPos((p) => Math.max(3, p - 5));
          if (e.key === "ArrowRight") setPos((p) => Math.min(97, p + 5));
        }}
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 bg-ember flex items-center justify-center cursor-ew-resize focus:outline-none focus:ring-2 focus:ring-ember focus:ring-offset-2 focus:ring-offset-coal"
        style={{ left: `${pos}%` }}
      >
        <ChevronsLeftRight size={18} className="text-coal" strokeWidth={2} />
      </div>
    </div>
  );
}

export default function BeforeAfter() {
  const [active, setActive] = useState(0);
  const job = GALLERY_JOBS[active];

  return (
    <section
      id="work"
      data-testid="gallery-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 border-t border-line"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 md:mb-20">
        <div>
          <Reveal>
            <SectionLabel>Before and After</SectionLabel>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-8">
              The before <span className="italic text-smoke">and the</span>{" "}
              <span className="italic text-ember">after.</span>
            </h2>
          </Reveal>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-4 flex flex-col">
          {GALLERY_JOBS.map((j, i) => (
            <div
              key={j.testid}
              data-testid={j.testid}
              role="button"
              tabIndex={0}
              onClick={() => setActive(i)}
              onKeyDown={(e) => e.key === "Enter" && setActive(i)}
              className={`group text-left py-7 border-t border-line last:border-b grid grid-cols-[auto,1fr] gap-6 transition-colors duration-300 cursor-pointer ${
                i === active ? "bg-ash/60" : "hover:bg-ash/30"
              }`}
            >
              <span
                className={`font-serif font-light text-3xl leading-none pl-1 transition-colors duration-300 ${
                  i === active ? "text-ember" : "text-outline-ember"
                }`}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <span>
                <span className="flex items-baseline justify-between gap-4">
                  <span className="font-serif text-xl md:text-2xl font-normal tracking-tight">
                    {j.title}
                  </span>
                </span>
                <span className="block text-smoke font-light text-sm mt-2 leading-relaxed">
                  {j.scope}
                </span>
                <button
                  data-testid={`${j.testid}-cta`}
                  onClick={(e) => {
                    e.stopPropagation();
                    scrollToId("#quote");
                  }}
                  className={`mt-4 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-semibold text-ember hover:gap-3.5 transition-[gap,opacity] duration-300 ${
                    i === active ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                  }`}
                >
                  Get this transformation
                  <ArrowUpRight size={13} strokeWidth={1.5} />
                </button>
              </span>
            </div>
          ))}
          <p className="hidden lg:block text-[10px] uppercase tracking-[0.3em] text-smoke/60 mt-8">
            Drag the handle to compare
          </p>
        </div>

        <div className="lg:col-span-8">
          <Reveal delay={0.15} y={48}>
            <AnimatePresence mode="wait">
              <motion.div
                key={job.testid}
                initial={{ opacity: 0, scale: 0.985 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.985 }}
                transition={{ duration: 0.5, ease: EASE }}
              >
                <CompareSlider job={job} />
              </motion.div>
            </AnimatePresence>
            <p className="lg:hidden text-[10px] uppercase tracking-[0.3em] text-smoke/60 mt-6">
              Drag the handle to compare
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
